/* Assistant « purifier et recharger une pierre » — DailyLama
   Maquette : Claude Design « Rituel des pierres.dc.html ».

   Principe directeur : le doute produit du repli, jamais une recommandation
   optimiste. Une ligne sans source gemmologique reste `unknown`, et `unknown`
   ne peut jamais être promu en `allowed`.

   Deux couches : moteur pur (testable, sans DOM) et branchement DOM. */
(function (global) {
  'use strict';

  /* Ordre de sévérité : blocked > unknown > conditional > allowed.
     `unknown` est délibérément plus sévère que `conditional` — ne rien savoir
     est plus inquiétant que savoir que c'est possible sous conditions. */
  var SEVERITE = { allowed: 0, conditional: 1, unknown: 2, blocked: 3 };
  var METHODES = ['water', 'directSalt', 'directSun', 'heat', 'smoke', 'steam', 'ultrasonic'];

  /* Jamais proposées comme rituel, quelle que soit la pierre. */
  var JAMAIS_RITUEL = ['heat', 'steam', 'ultrasonic', 'directSalt'];

  function plusSevere(a, b) {
    return SEVERITE[a] >= SEVERITE[b] ? a : b;
  }

  /* Normalisation pour la recherche : accents, œ/oe, tirets, espaces, casse. */
  function normaliser(texte) {
    return String(texte == null ? '' : texte)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/œ/gi, 'oe')
      .replace(/æ/gi, 'ae')
      .toLowerCase()
      .replace(/[\s\-_']+/g, ' ')
      .trim();
  }

  function indexer(matrice) {
    var index = {};
    function ajouter(pierre, verifiee) {
      var entree = { id: pierre.id, label: pierre.label, verifiee: verifiee, brut: pierre };
      index[normaliser(pierre.id)] = entree;
      index[normaliser(pierre.label)] = entree;
      (pierre.aliases || []).forEach(function (a) { index[normaliser(a)] = entree; });
    }
    (matrice.stones || []).forEach(function (p) { ajouter(p, p.reviewStatus === 'verified'); });
    (matrice.pendingStones || []).forEach(function (p) { ajouter(p, false); });
    return index;
  }

  /* Formats qui ajoutent leurs propres restrictions à celles de la pierre. */
  var SUPPORTS = {
    loose: { restrictions: {}, hypothese: null },
    jewelry: {
      restrictions: { water: 'conditional', ultrasonic: 'blocked', steam: 'blocked' },
      hypothese: 'Le montage — métal, élastique, colle — réagit souvent avant la pierre.'
    },
    mala: {
      restrictions: { water: 'blocked', ultrasonic: 'blocked', steam: 'blocked', smoke: 'conditional' },
      hypothese: 'Un fil, des nœuds et un pompon retiennent l’eau et sèchent mal.'
    },
    mixed: { restrictions: {}, hypothese: 'Plusieurs pierres : c’est la plus fragile qui commande.' },
    unknown: { restrictions: {}, hypothese: null }
  };

  var REPLI = {
    label: 'Méthode la plus douce',
    purification: [
      'Gardez la pierre ou le bijou au sec, sur un tissu propre.',
      'Tenez-la à l’écart du soleil direct et de toute source de chaleur.',
      'Un temps de repos suffit : aucun contact avec l’eau, le sel ou la fumée.'
    ],
    recharge: [
      'Posez une intention, en silence, sans manipuler la pierre.',
      'Un son doux à distance convient, sans contact ni vibration transmise.',
      'Vérifiez la composition, les traitements et les conseils du vendeur avant tout nettoyage.'
    ]
  };

  var CONSEIL_PRO = 'Pour préserver cet objet, demandez l’avis d’un bijoutier, gemmologue ou restaurateur avant tout nettoyage ou rituel avec contact.';

  /* Moteur de décision. Fonction pure : mêmes entrées, même sortie, aucun effet de bord.
     Renvoie toujours un résultat complet — le repli est une fonctionnalité, pas une erreur. */
  function getStoneCareResult(entree, matrice) {
    entree = entree || {};
    var objectType = SUPPORTS[entree.objectType] ? entree.objectType : 'unknown';
    var ids = Array.isArray(entree.stoneIds) ? entree.stoneIds.slice() : [];
    var complications = Array.isArray(entree.complications) ? entree.complications : [];

    var resultat = {
      stoneIds: [], stoneLabels: [], objectType: objectType,
      fallbackUsed: false, assumptions: [], reasons: [],
      symbolicPurification: null, symbolicRecharge: null,
      physicalCare: {}, guideUrl: null, productLinks: []
    };

    /* Matrice absente, illisible ou vide : repli, sans exception. */
    if (!matrice || typeof matrice !== 'object' || !Array.isArray(matrice.stones)) {
      resultat.reasons.push('Les données d’entretien ne sont pas disponibles.');
      return replier(resultat, false, null);
    }

    var index = indexer(matrice);

    /* Un seul choix hors « assemblage multi-pierres ». */
    if (objectType !== 'mixed' && ids.length > 1) ids = ids.slice(0, 1);

    var pierres = [];
    var inconnue = ids.length === 0;
    ids.forEach(function (brut) {
      var e = index[normaliser(brut)];
      if (!e) {
        inconnue = true;
        resultat.reasons.push('Pierre non reconnue : nous restons sur la méthode la plus douce.');
        return;
      }
      resultat.stoneIds.push(e.id);
      resultat.stoneLabels.push(e.label);
      if (!e.verifiee) {
        inconnue = true;
        resultat.reasons.push(e.label + ' : consignes matérielles pas encore validées par une source gemmologique.');
        return;
      }
      pierres.push(e.brut);
    });

    /* Placage, colle, cuir, textile, pièce ancienne ou fissurée : repli et avis d'un professionnel. */
    var fragile = complications.indexOf('fragile') >= 0;
    var montage = complications.indexOf('montage') >= 0;
    if (montage) {
      resultat.reasons.push('Un placage, une colle, un cuir ou une finition inconnue réagit avant la pierre elle-même.');
    }
    if (fragile) {
      resultat.reasons.push(CONSEIL_PRO);
    }

    if (inconnue || montage || fragile || !pierres.length) {
      var guideRepli = null;
      if (resultat.stoneIds.length === 1) {
        var e = index[normaliser(resultat.stoneIds[0])];
        if (e && e.brut) guideRepli = e.brut.guideUrl;
      }
      return replier(resultat, fragile, guideRepli);
    }

    /* Une pierre verrouille chaque méthode à son état le plus sévère,
       puis le support ajoute les siennes par-dessus. */
    METHODES.forEach(function (m) {
      var etat = 'allowed';
      var motifs = [];
      pierres.forEach(function (p) {
        var regle = (p.physicalCare || {})[m] || { status: 'unknown', reason: 'Donnée absente.' };
        var avant = etat;
        etat = plusSevere(etat, regle.status);
        if (etat !== avant || pierres.length === 1) {
          motifs.push({ pierre: p.label, statut: regle.status, motif: regle.reason });
        }
      });
      var support = SUPPORTS[objectType].restrictions[m];
      if (support) {
        var avantSupport = etat;
        etat = plusSevere(etat, support);
        if (etat !== avantSupport) {
          motifs.push({ pierre: 'Le montage', statut: support, motif: SUPPORTS[objectType].hypothese });
        }
      }
      resultat.physicalCare[m] = { status: etat, details: motifs };
    });

    /* Le sel direct reste bloqué quoi qu'il arrive : aucune source ne le recommande. */
    resultat.physicalCare.directSalt = {
      status: 'blocked',
      details: resultat.physicalCare.directSalt.details.length
        ? resultat.physicalCare.directSalt.details
        : [{ pierre: 'Règle générale', statut: 'blocked', motif: 'Le sel direct n’est proposé pour aucune pierre.' }]
    };

    if (SUPPORTS[objectType].hypothese) resultat.assumptions.push(SUPPORTS[objectType].hypothese);
    if (pierres.length > 1) resultat.assumptions.push('C’est l’état le plus strict de l’ensemble qui s’applique.');

    resultat.symbolicPurification = ritesDe(pierres, 'purification');
    resultat.symbolicRecharge = ritesDe(pierres, 'recharge');

    /* Guide et produits seulement s'ils sont vérifiés — sinon les modules restent masqués. */
    var guide = pierres.length === 1 ? pierres[0].guideUrl : null;
    resultat.guideUrl = (typeof guide === 'string' && guide.indexOf('https://') === 0) ? guide : null;
    resultat.productLinks = [];

    return resultat;
  }

  /* `guides` est passé à part : une pierre en attente n'a pas de consignes
     matérielles, mais son guide éditorial reste utile et vérifié. */
  function replier(resultat, avecProfessionnel, guides) {
    resultat.fallbackUsed = true;
    resultat.symbolicPurification = { label: REPLI.label, methodId: 'dry-rest', steps: REPLI.purification.slice() };
    resultat.symbolicRecharge = { label: REPLI.label, methodId: 'intention', steps: REPLI.recharge.slice() };
    /* Le repli interdit tout : immersion, sel, chaleur, soleil, solvant, flamme,
       vapeur, ultrasons. Aucune méthode ne peut y être « tolérée ». */
    METHODES.forEach(function (m) {
      resultat.physicalCare[m] = {
        status: 'blocked',
        details: [{ pierre: 'Méthode la plus douce', statut: 'blocked',
                    motif: 'Sans donnée validée, aucun contact n’est proposé.' }]
      };
    });
    resultat.guideUrl = (typeof guides === 'string' && guides.indexOf('https://') === 0) ? guides : null;
    resultat.productLinks = [];
    if (avecProfessionnel && resultat.reasons.indexOf(CONSEIL_PRO) < 0) resultat.reasons.push(CONSEIL_PRO);
    return resultat;
  }

  /* Intersection des rituels : seul ce qui convient à TOUTES les pierres est proposé.
     Les méthodes matérielles risquées en sont exclues par construction. */
  function ritesDe(pierres, type) {
    var commun = null;
    pierres.forEach(function (p) {
      var liste = ((p.symbolicRituals || {})[type] || []).filter(function (r) {
        return JAMAIS_RITUEL.indexOf(r) < 0;
      });
      commun = commun === null ? liste.slice() : commun.filter(function (r) { return liste.indexOf(r) >= 0; });
    });
    if (!commun || !commun.length) {
      return { label: REPLI.label, methodId: 'dry-rest',
               steps: (type === 'purification' ? REPLI.purification : REPLI.recharge).slice() };
    }
    var TEXTES = {
      'dry-rest': 'Laissez la pierre reposer au sec, sur un tissu propre, à l’écart de la lumière directe.',
      'indirect-light': 'Une lumière douce et indirecte suffit — jamais le soleil direct.',
      'gentle-sound': 'Un son doux à distance, sans contact ni vibration transmise à la pierre.',
      'intention': 'Formulez une intention, en silence, sans manipuler la pierre.'
    };
    return {
      label: 'Rituel symbolique',
      methodId: commun[0],
      steps: commun.map(function (r) { return TEXTES[r] || r; })
    };
  }

  global.DailyLamaRituelPierres = {
    getStoneCareResult: getStoneCareResult,
    normaliser: normaliser,
    plusSevere: plusSevere,
    SEVERITE: SEVERITE,
    METHODES: METHODES,
    REPLI: REPLI,
    CONSEIL_PRO: CONSEIL_PRO
  };
})(typeof window !== 'undefined' ? window : globalThis);

/* ------------------------------------------------------------------ */
/* Couche DOM — séparée du moteur, qui reste pur et testable            */
/* ------------------------------------------------------------------ */
(function (global) {
  'use strict';
  if (typeof document === 'undefined') return;

  var API = global.DailyLamaRituelPierres;

  var LIBELLES = {
    water: 'Eau', directSalt: 'Sel direct', directSun: 'Soleil direct',
    heat: 'Chaleur', smoke: 'Fumée', steam: 'Vapeur', ultrasonic: 'Ultrasons'
  };
  var ETATS = {
    blocked: 'À éviter', unknown: 'À vérifier', conditional: 'Sous conditions', allowed: 'Toléré'
  };

  /* Mesure : uniquement des valeurs contrôlées par la matrice. Aucun texte libre,
     aucune saisie brute, aucune URL. Sans transport canonique vérifié, on se contente
     du dataLayer et d'un CustomEvent : aucune requête réseau n'est émise. */
  function pister(nom, props) {
    var permis = ['stone_id', 'object_type', 'fallback_used', 'guide_available', 'products_available'];
    var propres = {};
    Object.keys(props || {}).forEach(function (k) {
      if (permis.indexOf(k) >= 0) propres[k] = props[k];
    });
    try {
      global.dataLayer = global.dataLayer || [];
      global.dataLayer.push(Object.assign({ event: nom }, propres));
    } catch (e) {}
    try {
      global.dispatchEvent(new CustomEvent('dl:rituel-pierres', { detail: { nom: nom, props: propres } }));
    } catch (e) {}
  }

  function monter(racine) {
    if (!racine || racine.dataset.dlrpMonte === 'oui') return null;
    racine.dataset.dlrpMonte = 'oui';
    racine.setAttribute('data-js', 'oui');

    var matrice = null;
    try {
      var bloc = racine.querySelector('[data-dlrp-matrice]');
      matrice = bloc ? JSON.parse(bloc.textContent) : null;
    } catch (e) { matrice = null; }

    var $ = function (s) { return racine.querySelector(s); };
    var $$ = function (s) { return Array.prototype.slice.call(racine.querySelectorAll(s)); };

    var etapes = { 1: $('[data-dlrp-etape="1"]'), 2: $('[data-dlrp-etape="2"]'), 3: $('[data-dlrp-etape="3"]') };
    var champ = $('[data-dlrp-recherche]');
    var liste = $('[data-dlrp-liste]');
    var jetons = $('[data-dlrp-jetons]');
    var annonce = $('[data-dlrp-annonce]');
    var manque1 = $('[data-dlrp-manque="1"]');
    var manque2 = $('[data-dlrp-manque="2"]');

    var etat = { format: null, complications: [], pierres: [], resultat: null };

    /* Index de recherche construit une fois : aucun appel réseau à la frappe. */
    var catalogue = [];
    if (matrice) {
      (matrice.stones || []).forEach(function (p) { catalogue.push({ id: p.id, label: p.label, verifiee: true, aliases: p.aliases || [] }); });
      (matrice.pendingStones || []).forEach(function (p) { catalogue.push({ id: p.id, label: p.label, verifiee: false, aliases: p.aliases || [] }); });
    }
    catalogue.forEach(function (c) {
      c.cles = [c.label].concat(c.aliases).map(API.normaliser);
    });

    function montrer(n) {
      Object.keys(etapes).forEach(function (k) { if (etapes[k]) etapes[k].hidden = String(k) !== String(n); });
      var jauge = $('[data-dlrp-jauge]');
      if (jauge) jauge.textContent = 'Étape ' + n + ' sur 3';
    }

    function texte(el, v) { if (el) el.textContent = v; }

    function peindreFormats() {
      /* Motif ARIA « radiogroup » : un seul bouton reste tabulable, celui qui est
         coché — ou le premier tant que rien ne l'est. Sans cette mise à jour, le
         tabindex figé du gabarit rendrait les autres options inatteignables. */
      var boutons = $$('[data-dlrp-format]');
      var coche = -1;
      boutons.forEach(function (b, i) {
        var actif = b.dataset.dlrpFormat === etat.format;
        b.setAttribute('aria-checked', actif ? 'true' : 'false');
        if (actif) coche = i;
      });
      boutons.forEach(function (b, i) {
        b.tabIndex = (i === (coche === -1 ? 0 : coche)) ? 0 : -1;
      });
      texte(manque1, etat.format ? '' : 'Choisissez d’abord un format pour continuer.');
      var suivant = $('[data-dlrp-vers2]');
      if (suivant) suivant.disabled = !etat.format;
    }

    function peindreJetons() {
      if (!jetons) return;
      jetons.textContent = '';
      etat.pierres.forEach(function (p) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'dlrp__jeton';
        b.textContent = p.label;
        var croix = document.createElement('span');
        croix.setAttribute('aria-hidden', 'true');
        croix.textContent = '×';
        var lu = document.createElement('span');
        lu.className = 'dlrp__cache';
        lu.textContent = 'Retirer ' + p.label;
        b.appendChild(croix); b.appendChild(lu);
        b.addEventListener('click', function () {
          etat.pierres = etat.pierres.filter(function (x) { return x.id !== p.id; });
          peindreJetons(); peindreRecherche();
        });
        jetons.appendChild(b);
      });
      var pret = etat.pierres.length > 0;
      var lancer = $('[data-dlrp-lancer]');
      if (lancer) lancer.disabled = !pret;
      texte(manque2, pret ? '' : 'Sélectionnez au moins une pierre, ou indiquez que vous ne la connaissez pas.');
    }

    function peindreRecherche() {
      if (!liste) return;
      liste.textContent = '';
      var q = API.normaliser(champ ? champ.value : '');
      var trouves = q.length < 2
        ? catalogue.slice(0, 6)
        : catalogue.filter(function (c) {
            return c.cles.some(function (k) { return k.indexOf(q) >= 0; });
          }).slice(0, 8);

      if (!trouves.length) {
        var vide = document.createElement('p');
        vide.className = 'dlrp__vide';
        vide.textContent = 'Aucune pierre ne correspond. Vérifiez l’orthographe, ou choisissez « Je ne connais pas ma pierre » pour obtenir la méthode la plus douce.';
        liste.appendChild(vide);
        return;
      }

      trouves.forEach(function (c) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'dlrp__option';
        b.setAttribute('role', 'option');
        var choisie = etat.pierres.some(function (p) { return p.id === c.id; });
        b.setAttribute('aria-selected', choisie ? 'true' : 'false');
        var nom = document.createElement('span');
        nom.textContent = c.label;
        var etatEl = document.createElement('span');
        etatEl.className = 'dlrp__option-etat';
        /* Le statut des données est dit en toutes lettres, pas seulement par une couleur. */
        etatEl.textContent = c.verifiee ? 'Consignes validées' : 'Méthode douce uniquement';
        b.appendChild(nom); b.appendChild(etatEl);
        b.addEventListener('click', function () {
          if (etat.format === 'mixed') {
            if (!choisie) etat.pierres.push({ id: c.id, label: c.label });
          } else {
            etat.pierres = [{ id: c.id, label: c.label }];
          }
          peindreJetons(); peindreRecherche();
        });
        liste.appendChild(b);
      });
    }

    function ligne(methode, donnee) {
      var l = document.createElement('div');
      l.className = 'dlrp__precaution';
      var nom = document.createElement('span');
      nom.className = 'dlrp__precaution-nom';
      nom.textContent = LIBELLES[methode];
      var et = document.createElement('span');
      et.className = 'dlrp__precaution-etat';
      et.setAttribute('data-etat', donnee.status);
      et.textContent = ETATS[donnee.status];
      var motif = document.createElement('span');
      motif.className = 'dlrp__precaution-motif';
      /* Chaque précaution porte son explication : jamais un verdict sec. */
      motif.textContent = (donnee.details || [])
        .map(function (d) { return d.motif; }).filter(Boolean).join(' ');
      l.appendChild(nom); l.appendChild(et); l.appendChild(motif);
      return l;
    }

    function rituel(cible, bloc) {
      var el = $(cible);
      if (!el) return;
      el.textContent = '';
      (bloc.steps || []).forEach(function (s) {
        var li = document.createElement('li');
        li.textContent = s;
        el.appendChild(li);
      });
    }

    /* Recalcul complet : l'ancien résultat est effacé avant d'écrire le nouveau,
       de sorte qu'aucun conseil obsolète ne puisse subsister à l'écran. */
    function afficherResultat() {
      var r = API.getStoneCareResult({
        objectType: etat.format,
        stoneIds: etat.pierres.map(function (p) { return p.id; }),
        complications: etat.complications
      }, matrice);
      etat.resultat = r;

      texte($('[data-dlrp-resultat-titre]'),
        r.stoneLabels.length ? r.stoneLabels.join(' + ') : 'Pierre non identifiée');

      var hyp = $('[data-dlrp-hypotheses]');
      if (hyp) {
        hyp.textContent = '';
        r.assumptions.concat(r.reasons).forEach(function (t) {
          var li = document.createElement('li');
          li.textContent = t;
          hyp.appendChild(li);
        });
        hyp.hidden = !hyp.children.length;
      }

      var avis = $('[data-dlrp-avis]');
      if (avis) avis.hidden = !r.fallbackUsed;

      var prec = $('[data-dlrp-precautions]');
      if (prec) {
        prec.textContent = '';
        API.METHODES.forEach(function (m) { prec.appendChild(ligne(m, r.physicalCare[m])); });
      }

      rituel('[data-dlrp-purification]', r.symbolicPurification);
      rituel('[data-dlrp-recharge]', r.symbolicRecharge);

      /* Modules vides masqués : ni guide ni produit tant que rien n'est vérifié. */
      var guide = $('[data-dlrp-guide]');
      if (guide) {
        /* Sur une page pierre, le guide pointerait vers la page déjà ouverte :
           on masque plutôt que de proposer un lien qui ne mène nulle part. */
        var ici = global.location ? global.location.href.split('#')[0].split('?')[0] : '';
        var memePage = r.guideUrl && ici && r.guideUrl.replace(/\/$/, '') === ici.replace(/\/$/, '');
        guide.hidden = !r.guideUrl || !!memePage;
        if (r.guideUrl) guide.href = r.guideUrl;
      }
      var produits = $('[data-dlrp-produits]');
      if (produits) produits.hidden = !r.productLinks.length;

      var conf = $('[data-dlrp-confirmation]');
      if (conf) conf.hidden = true;

      montrer(3);
      /* Le bouton qui avait le focus vient d'être masqué : sans reprise, la
         tabulation repartirait du haut du document. On pose le focus sur le
         titre du résultat, sans l'ajouter au parcours de tabulation. */
      var titre = $('[data-dlrp-resultat-titre]');
      if (titre && typeof titre.focus === 'function') {
        titre.setAttribute('tabindex', '-1');
        try { titre.focus({ preventScroll: false }); } catch (e) { titre.focus(); }
      }
      if (annonce) {
        annonce.textContent = r.fallbackUsed
          ? 'Résultat prudent affiché : méthode la plus douce.'
          : 'Résultat affiché pour ' + r.stoneLabels.join(' et ') + '.';
      }
      pister('stone_care_result', {
        object_type: r.objectType,
        stone_id: r.stoneIds.length === 1 ? r.stoneIds[0] : null,
        fallback_used: r.fallbackUsed,
        guide_available: !!r.guideUrl,
        products_available: r.productLinks.length > 0
      });
    }

    /* ---------- Écoutes ---------- */

    function choisirFormat(b) {
      etat.format = b.dataset.dlrpFormat;
      if (etat.format !== 'mixed' && etat.pierres.length > 1) etat.pierres = etat.pierres.slice(0, 1);
      peindreFormats();
    }

    var PAS = { ArrowDown: 1, ArrowRight: 1, Down: 1, Right: 1, ArrowUp: -1, ArrowLeft: -1, Up: -1, Left: -1 };

    $$('[data-dlrp-format]').forEach(function (b, i, tous) {
      b.addEventListener('click', function () { choisirFormat(b); });

      /* Espace et Entrée passent déjà par le clic natif du bouton. Restent les
         déplacements : dans un radiogroup, la flèche déplace le focus et coche. */
      b.addEventListener('keydown', function (ev) {
        var cible = null;
        if (Object.prototype.hasOwnProperty.call(PAS, ev.key)) {
          cible = tous[(i + PAS[ev.key] + tous.length) % tous.length];
        } else if (ev.key === 'Home') {
          cible = tous[0];
        } else if (ev.key === 'End') {
          cible = tous[tous.length - 1];
        }
        if (!cible) return;
        ev.preventDefault();
        choisirFormat(cible);
        cible.focus();
      });
    });

    $$('[data-dlrp-complication]').forEach(function (c) {
      c.addEventListener('change', function () {
        var id = c.dataset.dlrpComplication;
        etat.complications = c.checked
          ? etat.complications.concat([id])
          : etat.complications.filter(function (x) { return x !== id; });
      });
    });

    var vers2 = $('[data-dlrp-vers2]');
    if (vers2) vers2.addEventListener('click', function () {
      if (!etat.format) return;
      if (preselection && etat.pierres.length && etat.format !== 'mixed') {
        afficherResultat();
        return;
      }
      montrer(2);
      peindreJetons();
      peindreRecherche();
      if (champ) champ.focus();
      pister('stone_care_tool_start', { object_type: etat.format });
    });

    var retour = $('[data-dlrp-retour]');
    if (retour) retour.addEventListener('click', function () {
      montrer(1);
      peindreFormats();
    });

    if (champ) champ.addEventListener('input', peindreRecherche);

    var lancer = $('[data-dlrp-lancer]');
    if (lancer) lancer.addEventListener('click', function () {
      if (!etat.pierres.length) return;
      afficherResultat();
    });

    $$('[data-dlrp-inconnue]').forEach(function (b) {
      b.addEventListener('click', function () {
        etat.pierres = [];
        afficherResultat();
      });
    });

    var modifier = $('[data-dlrp-modifier]');
    if (modifier) modifier.addEventListener('click', function () {
      montrer(1);
      peindreFormats();
    });

    var recommencer = $('[data-dlrp-recommencer]');
    if (recommencer) recommencer.addEventListener('click', function () {
      etat = { format: null, complications: [], pierres: [], resultat: null };
      if (preselection) {
        var re = catalogue.filter(function (c) { return c.id === preselection; })[0];
        if (re) etat.pierres = [{ id: re.id, label: re.label }];
      }
      $$('[data-dlrp-complication]').forEach(function (c) { c.checked = false; });
      if (champ) champ.value = '';
      if (annonce) annonce.textContent = '';
      montrer(1);
      peindreFormats();
      peindreJetons();
      pister('stone_care_restart', {});
    });

    var copier = $('[data-dlrp-copier]');
    if (copier) copier.addEventListener('click', function () {
      var r = etat.resultat;
      if (!r) return;
      var lignes = ['DailyLama — rituel et précautions'];
      if (r.stoneLabels.length) lignes.push('Pierre : ' + r.stoneLabels.join(', '));
      API.METHODES.forEach(function (m) {
        lignes.push(LIBELLES[m] + ' : ' + ETATS[r.physicalCare[m].status]);
      });
      var conf = $('[data-dlrp-confirmation]');
      var fini = function (msg) { if (conf) { conf.textContent = msg; conf.hidden = false; } };
      if (global.navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(lignes.join('\n'))
          .then(function () { fini('Récapitulatif copié.'); })
          .catch(function () { fini('La copie n’a pas abouti.'); });
      } else {
        fini('La copie n’est pas disponible sur ce navigateur.');
      }
    });

    /* Sur une page dédiée à une pierre, elle est déjà connue : le visiteur n'a plus
       qu'à préciser le format. L'étape de recherche reste accessible par « Modifier ». */
    var preselection = racine.getAttribute('data-dlrp-pierre');
    if (preselection) {
      var trouvee = catalogue.filter(function (c) { return c.id === preselection; })[0];
      if (trouvee) {
        etat.pierres = [{ id: trouvee.id, label: trouvee.label }];
        var titre = $('[data-dlrp-titre-preselection]');
        if (titre) titre.hidden = false;
      }
    }

    /* Les chiffres annoncés viennent de la matrice, jamais d'une valeur écrite à la main :
       une pierre ajoutée ou retirée doit se voir immédiatement dans le hero. */
    var nombres = {
      etapes: 3,
      pierres: catalogue.length,
      methodes: (global.DailyLamaRituelPierres.METHODES || []).length
    };
    Object.keys(nombres).forEach(function (cle) {
      var el = $('[data-dlrp-nb="' + cle + '"]');
      if (el) el.textContent = String(nombres[cle]);
    });

    /* Le bouton du hero amène au premier choix et y pose le focus : sans cela, il
       ferait défiler sans rien donner à faire au clavier. */
    var demarrer = $('[data-dlrp-demarrer]');
    if (demarrer) {
      demarrer.addEventListener('click', function () {
        var premier = $('[data-dlrp-format]');
        if (!premier) return;
        var doux = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (premier.scrollIntoView) {
          premier.scrollIntoView({ block: 'center', behavior: doux ? 'auto' : 'smooth' });
        }
        premier.focus({ preventScroll: true });
      });
    }

    montrer(1);
    peindreFormats();
    pister('stone_care_tool_view', preselection ? { stone_id: preselection } : {});
    return etat;
  }

  global.DailyLamaRituelPierres.monter = monter;

  /* Sur un article, la section se rend après tout le corps du texte. On la replace
     dans l'article, à une profondeur exprimée en paragraphes : le même pourcentage
     tombe au même endroit relatif, que l'article fasse 14 ou 76 paragraphes.
     Conteneur introuvable — page pierre, gabarit inattendu — on ne touche à rien. */
  /* Paragraphes de tolérance pour accrocher un titre plutôt qu'un paragraphe. */
  var TOLERANCE_TITRE = 2;

  function replacer(racine) {
    var vers = racine.getAttribute('data-dlrp-vers');
    if (!vers) return;
    var profondeur = parseInt(racine.getAttribute('data-dlrp-profondeur') || '0', 10);
    if (!(profondeur > 0)) return;

    /* C'est l'enveloppe de section qu'il faut déplacer, pas la seule balise <section> :
       sinon on la sortirait de son conteneur et de ses styles. */
    var bloc = racine.closest ? (racine.closest('.shopify-section') || racine) : racine;

    var conteneur = null;
    var pistes = vers.split(',');
    for (var i = 0; i < pistes.length && !conteneur; i++) {
      conteneur = document.querySelector(pistes[i].trim());
    }
    if (!conteneur || conteneur.contains(bloc)) return;

    /* Le corps de l'article n'est pas toujours plat : selon la mise en page, les
       paragraphes sont au premier niveau ou enveloppés dans un div. On cherche donc
       le vrai conteneur de flux — celui qui porte le plus de paragraphes — au lieu
       de supposer une profondeur. Les paragraphes de l'outil lui-même sont écartés,
       sinon la profondeur se décale dès qu'il a été replacé une première fois. */
    var candidats = Array.prototype.slice.call(
      conteneur.querySelectorAll(':scope > p, :scope > div > p, :scope > div > div > p')
    ).filter(function (e) { return !bloc.contains(e); });
    if (!candidats.length) return;

    var comptes = [];
    candidats.forEach(function (e) {
      var trouve = null;
      for (var k = 0; k < comptes.length; k++) if (comptes[k].parent === e.parentNode) trouve = comptes[k];
      if (!trouve) comptes.push({ parent: e.parentNode, n: 1 });
      else trouve.n++;
    });
    comptes.sort(function (a, b) { return b.n - a.n; });
    var flux = comptes[0].parent;

    var enfants = Array.prototype.slice.call(flux.children).filter(function (e) {
      return !bloc.contains(e) && e !== bloc;
    });
    var paras = enfants.filter(function (e) { return e.tagName === 'P'; });
    if (!paras.length) return;

    var vise = Math.max(1, Math.min(paras.length, Math.round(paras.length * profondeur / 100)));

    /* Un ratio brut tombe volontiers au milieu d'un raisonnement. On préfère la
       couture nette : le premier titre situé au niveau visé ou après. À défaut de
       titre, on retombe sur le paragraphe visé. */
    var vus = 0, ancre = null, avant = true;
    for (var j = 0; j < enfants.length; j++) {
      var e = enfants[j];
      if (e.tagName === 'P') vus++;
      /* Le titre n'est un bon point de couture que s'il est proche : quand le suivant
         est à dix paragraphes, s'y accrocher enfonce l'outil bien plus bas que la
         profondeur demandée. Au-delà de la tolérance, on coupe au paragraphe visé. */
      if (vus >= vise && /^H[23]$/.test(e.tagName) && vus <= vise + TOLERANCE_TITRE) {
        ancre = e; avant = true; break;
      }
      if (vus > vise + TOLERANCE_TITRE) break;
    }
    if (!ancre) { ancre = paras[vise - 1]; avant = false; }
    if (!ancre || !ancre.parentNode) return;

    ancre.parentNode.insertBefore(bloc, avant ? ancre : ancre.nextSibling);
  }

  var demarrage = function () {
    Array.prototype.slice.call(document.querySelectorAll('[data-dlrp-racine]')).forEach(function (r) {
      replacer(r);
      monter(r);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrage);
  else demarrage();
})(typeof window !== 'undefined' ? window : globalThis);
